import { loadingController, modalController, toastController } from '@ionic/core';
import { Component, Event, Fragment, h, Prop, State } from '@stencil/core';
import { PersonDoc, UserForm } from 'common';
import { User } from '../../../global/models';
import { AuthService } from '../../../global/service/AuthService';
import { isValidEmail } from '../../../global/utils/data';
import { refreshSessionOrShowAlert } from 'src/global/service/interceptors';

@Component({
  tag: 'request-access',
  styleUrl: '../concourse-login.css',
})
export class RequestAccess {
  static async checkRolesAreDifferent(requiredRoles: string[], userFromPeople: PersonDoc, currentUser: PersonDoc): Promise<boolean> {
    if (!userFromPeople) {
      return false;
    }

    const userFromPeopleRoles = requiredRoles.filter(role => userFromPeople.roles.includes(role));
    let currentUserRoles = requiredRoles.filter(role => currentUser.roles.includes(role));

    if (userFromPeopleRoles.length === 0 && currentUserRoles.length === 0) {
      return false;
    }

    // Check if the actual roles are different, not just the count
    const rolesAreDifferent = userFromPeopleRoles.length !== currentUserRoles.length || !userFromPeopleRoles.every(role => currentUserRoles.includes(role));
    if (rolesAreDifferent) {
      const preCheckRoles = await RequestAccess.preCheckMulitpleRoles(requiredRoles);
      return preCheckRoles;
    }

    // If roles match between DB and cached user, return true if user has any of the required roles
    return userFromPeopleRoles.length > 0;
  }

  static countRequiredRoles(requiredRoles: string[], user: PersonDoc) {
    return requiredRoles.filter(role => user.roles.includes(role)).length;
  }

  static async preCheckRole(requestedRole: string) {
    if (globalThis?.user?.doc._id.startsWith('org.couchdb.user:anon')) {
      // If the user is a guest, they cannot have any roles
      return false;
    }
    const session = await refreshSessionOrShowAlert();
    return session?.user?.roles?.includes(requestedRole);
  }

  static async preCheckMulitpleRoles(requestedRoles: string[]) {
    if (globalThis?.user?.doc._id.startsWith('org.couchdb.user:anon')) {
      // If the user is a guest, they cannot have any roles
      return false;
    }
    const session = await refreshSessionOrShowAlert();
    return session?.user?.roles?.some(role => requestedRoles.includes(role));
  }

  static async presentModal(projectId: string, requestedPage: string, requestedRole: string, callback: Function = null): Promise<void> {
    const modal = await modalController.create({
      component: 'request-access',
      cssClass: 'modal-height-auto',
      backdropDismiss: false,
      componentProps: { projectId, requestedPage, requestedRole, canDismiss: true },
    });

    modal.addEventListener('requested', () => {
      if (callback) callback(this);
      modal.dismiss();
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    return await modal.present();
  }
  @Prop() projectId: string;
  @Prop() requestedPage: string;
  @Prop() requestedRole: string;
  @Prop() canDismiss: boolean;

  @State() errorMessage: string;
  @State() responseMessage: string;
  @Event() dismiss;
  @Event() requested;

  private formData: UserForm;

  get user(): User {
    return globalThis.user;
  }

  componentWillLoad() {
    this.formData = {
      first_name: this.user.doc.first_name,
      last_name: this.user.doc.last_name,
      email: this.user.doc.email,
      company: this.user.doc.company,
      company_role: this.user.doc.company_role,
    };
  }

  onFormFieldInput(event) {
    this.formData = {
      ...this.formData,
      [event.target.name]: event.target.value,
    };
  }

  async onSubmitButtonClick() {
    if (!this.formData.first_name) {
      this.errorMessage = 'Please enter a first name';
    }
    if (!this.formData.last_name) {
      this.errorMessage = 'Email is a required field';
    }
    if (!this.formData.email) {
      this.errorMessage = 'Email is a required field';
    }
    if (!isValidEmail(this.formData.email)) {
      this.errorMessage = 'Email is invalid';
    }
    const loading = await loadingController.create({ message: 'Sending Request' });
    try {
      loading.present();
      await AuthService.requestAccess(this.projectId, this.requestedPage, this.requestedRole, this.formData);
      toastController.create({ message: `Your request has been sent, please wait for approval.`, duration: 3000 }).then(toast => toast.present());
      this.requested.emit();
    } catch (err) {
      this.errorMessage = err.message;
    } finally {
      loading.dismiss();
    }
  }

  render() {
    return (
      <Fragment>
        <ion-header>
          <ion-toolbar>
            <ion-title>Request Access to {this.requestedPage}</ion-title>
            {this.canDismiss && (
              <ion-buttons slot="end">
                <ion-button onclick={() => this.dismiss.emit()}>
                  <ion-icon name="close" />
                </ion-button>
              </ion-buttons>
            )}
          </ion-toolbar>
        </ion-header>
        <ion-page class="ion-padding">
          <ion-grid>
            <ion-row>
              <ion-col>
                To access {this.requestedPage} you will need approval from the project admins.
                <div>role: {this.requestedRole}</div>
              </ion-col>
            </ion-row>
            {this.user.isAnonymous && [
              <ion-row>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="first-name-field"
                    data-testid="request-access-first-name"
                    label="First Name"
                    name="first_name"
                    label-placement="stacked"
                    value={this.formData.first_name}
                    onInput={e => this.onFormFieldInput(e)}
                  ></ion-input>
                </ion-col>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="last-name-field"
                    label="Last Name"
                    name="last_name"
                    label-placement="stacked"
                    value={this.formData.last_name}
                    onInput={e => this.onFormFieldInput(e)}
                  ></ion-input>
                </ion-col>
              </ion-row>,
              <ion-row>
                <ion-col>
                  <ion-input
                    id="email-field"
                    label="Email *"
                    name="email"
                    label-placement="stacked"
                    value={this.formData.email}
                    onInput={e => this.onFormFieldInput(e)}
                  ></ion-input>
                </ion-col>
              </ion-row>,
              <ion-row>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="company-field"
                    label="Company"
                    name="company"
                    label-placement="stacked"
                    value={this.formData.company}
                    onInput={e => this.onFormFieldInput(e)}
                  ></ion-input>
                </ion-col>
                <ion-col size="12" sizeMd="6">
                  <ion-input
                    id="company-role-field"
                    label="Company Role"
                    name="company_role"
                    label-placement="stacked"
                    value={this.formData.company_role}
                    onInput={e => this.onFormFieldInput(e)}
                  ></ion-input>
                </ion-col>
              </ion-row>,
            ]}
            <ion-row>
              <ion-col>
                {this.errorMessage && <ion-text color="danger">{this.errorMessage}</ion-text>}
                <ion-button data-testid="request-access-submit" expand="block" class="ion-margin-top" onClick={() => this.onSubmitButtonClick()}>
                  Request Access
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ion-page>
      </Fragment>
    );
  }
}
